import React, { useEffect, useRef, useState } from 'react';
import { MessageCircle, Bot, Lightbulb } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { familyTranslations } from '../data/translations';

const DemoSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const { lang } = useLanguage();
  const t = familyTranslations[lang].demoData;

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setIsVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-24 md:py-32 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div
          className={`text-center max-w-2xl mx-auto mb-16 transition-all duration-700 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
        >
          <span className="text-xs font-bold tracking-[0.2em] uppercase text-[#5B4FE9]">
            {t.label}
          </span>
          <h2
            className="mt-4 text-3xl sm:text-4xl md:text-[44px] font-bold text-gray-900 leading-[1.15] tracking-tight"
            style={{ fontFamily: "'Inter', sans-serif" }}
          >
            {t.title}
          </h2>
          <p className="mt-5 text-lg text-gray-500 leading-relaxed">
            {t.subtitle}
          </p>
        </div>

        {/* Browser mockup */}
        <div
          className={`max-w-3xl mx-auto transition-all duration-1000 ${
            isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'
          }`}
          style={{ transitionDelay: '300ms' }}
        >
          <div className="rounded-2xl overflow-hidden shadow-2xl shadow-gray-200/80 border border-gray-100">
            {/* Browser chrome */}
            <div className="flex items-center gap-2 px-5 py-3 bg-[#1a1a2e]">
              <div className="w-3 h-3 rounded-full bg-red-400" />
              <div className="w-3 h-3 rounded-full bg-yellow-400" />
              <div className="w-3 h-3 rounded-full bg-green-400" />
              <div className="ml-4 px-3 py-1 rounded-md bg-white/10 text-xs text-gray-400 font-mono">
                {t.url}
              </div>
            </div>

            {/* Content */}
            <div className="bg-white p-6 md:p-8 space-y-6">
              {/* Input */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center">
                  <MessageCircle className="w-4 h-4 text-gray-400" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-gray-400 font-medium">{t.input.label}</p>
                  <div className="mt-2 px-4 py-3 bg-gray-50 rounded-lg border border-gray-100">
                    <p className="text-sm text-gray-700 font-mono">{t.input.value}</p>
                  </div>
                </div>
              </div>

              {/* Divider */}
              <div className="border-t border-gray-100" />

              {/* Feedback */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-[#5B4FE9]/10 flex items-center justify-center">
                  <Bot className="w-4 h-4 text-[#5B4FE9]" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-[#5B4FE9] font-semibold">{t.feedback.label}</p>
                  <div className="mt-2 px-4 py-3 bg-indigo-50/50 rounded-lg border border-indigo-100">
                    <p className="text-sm text-gray-700 leading-relaxed">{t.feedback.text}</p>
                  </div>
                </div>
              </div>

              {/* Suggestion */}
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 w-8 h-8 rounded-full bg-amber-50 flex items-center justify-center">
                  <Lightbulb className="w-4 h-4 text-amber-600" />
                </div>
                <div className="flex-1">
                  <p className="text-xs text-amber-600 font-semibold">{t.suggestion.label}</p>
                  <div className="mt-2 px-4 py-3 bg-amber-50/50 rounded-lg border border-amber-100">
                    <p className="text-sm text-gray-700 leading-relaxed">{t.suggestion.text}</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default DemoSection;
